'use client'
import { useState } from 'react'
import Section from 'components/Section'
import { useMemoizedContentGetter } from 'utils/contentful'
import { Grid, Button, AchieveTheme } from '@achieve/ascend'
import { Typography, MediaImageStandard } from 'components/Contentful'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'
import { ChevronUp, ChevronDown } from 'react-feather'
import styles from './FeaturedStory.module.scss'
import { Eyebrow } from 'components/Eyebrow'
import classNames from 'classnames'

const FeaturedStory = ({ content }) => {
  const { sectionTitle, title, bodyCopy, featuredStoryimage, quote } = useMemoizedContentGetter(
    content,
    ['sectionTitle', 'title', 'bodyCopy', 'featuredStoryimage', 'quote']
  )
  const isMobile = useViewportSmallerThan(BREAKPOINTS.md)
  const [expanded, setExpanded] = useState(false)
  const mediaImageContent = !isMobile
    ? featuredStoryimage?.mediaContent
    : featuredStoryimage?.mobileMediaContent || featuredStoryimage?.mediaContent

  const collapseHandler = () => {
    setExpanded((wasExpanded) => !wasExpanded)
  }

  return (
    <Section
      data-testid="featured-story-section"
      className={styles['section']}
      backgroundColor={AchieveTheme?.ascend?.colors?.neutral?.[25]}
    >
      <Grid container justifyContent="center">
        <Grid
          size={{ xxs: 6, xs: 11, sm: 10, md: 10, lg: 10, xl: 8 }}
          justifyContent="space-around"
          data-testid="featured-story-title-container"
        >
          <Eyebrow
            data-testid="featured-story-eyebrow"
            content={sectionTitle?.textContent}
            component="h3"
            classNameText={'bodySm font-bold!'}
          />
          <Typography
            data-testid="featured-story-title"
            className={classNames(styles['title'], 'displayMd lg:displayLg font-bold!')}
            content={title?.textContent}
            component="h2"
          />
        </Grid>
      </Grid>
      <Grid container data-testid="featured-story-grid-main-container">
        <Grid
          data-testid="featured-story-grid-container"
          container
          className={styles['card-grid']}
          spacing={0}
        >
          <Grid
            data-testid="featured-story-left"
            className={`${styles['grid-item']} ${styles['body']}`}
            size={{ xs: 12, lg: 6 }}
            order={{ xs: 2, lg: 1 }}
          >
            <div className={isMobile && !expanded ? styles['small-body'] : undefined}>
              <Typography
                data-testid="featured-story-body"
                content={bodyCopy?.textContent}
                variant="bodySm"
                component="p"
              />
            </div>
            {isMobile && (
              <Button
                endIcon={expanded ? <ChevronUp /> : <ChevronDown />}
                className={styles['collapse-button']}
                onClick={collapseHandler}
                variant="text"
                data-testid="featured-story-expand-button"
              >
                {expanded ? 'Show less' : 'Show more'}
              </Button>
            )}
          </Grid>
          <Grid
            data-testid="featured-story-left"
            className={styles['grid-item']}
            size={{ xs: 12, lg: 6 }}
            order={{ xs: 1, lg: 2 }}
          >
            {mediaImageContent && (
              <Grid size={{ xs: 12, lg: 12 }}>
                <div className={styles['block-img']}>
                  <MediaImageStandard
                    layout="fill"
                    objectFit="cover"
                    objectPosition="top right"
                    classNameContainer="contents"
                    fillRelativeWrapper={false}
                    preload={false}
                    quality={isMobile ? 75 : 100}
                    content={mediaImageContent}
                    alt={featuredStoryimage?.mediaAltText}
                  />
                  <div className={styles['content']}>
                    <span className={styles['open-quote']}>“</span>
                    <Typography
                      content={quote?.textContent}
                      color={AchieveTheme?.ascend?.colors?.neutral?.[0]}
                      data-testid="featured-story-quote"
                      className={'headingLg lg:displayXl font-light!'}
                    />
                  </div>
                </div>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Section>
  )
}
export { FeaturedStory }
export default FeaturedStory
